import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import useId from '@mui/utils/useId';
import type React from 'react';
import { useState } from 'react';
import NestedMenuItem from '/@/mui/nested-menu-item';
import { isMobile } from '../utils';
import NestedMenuItemMobile from './nested-menu-item-mobile';

interface MenuItemProps {
    label: string;
    onMenuItemClick?: (event: React.MouseEvent) => void;
    ItemIcon?: React.ReactNode;
    disabled?: boolean;
    menuItems?: MenuItemProps[];
}

interface NestedMenuProps {
    children: MenuItemProps[];
    Icon: React.Component;
    tooltipTitle?: string;
    menuProps?: object;
    direction?: 'left' | 'right';
}

export const NestedMenu: React.FC<NestedMenuProps> = ({
    menuItems,
    Icon,
    tooltipTitle,
    menuProps,
    direction = 'left',
}) => {
    const menuId = useId();
    const buttonId = useId();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleIconClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleItemClick = (item: MenuItemProps) => {
        if (item.onMenuItemClick) item.onMenuItemClick();
        handleMenuClose();
    };

    const renderMenuItems = (menuItems: MenuItemProps[], parentMenuOpen = false) => {
        return menuItems.map((item, index) =>
            item.children ? (
                !isMobile() ? (
                    <NestedMenuItem
                        key={`${index}-${item.label}`}
                        label={item.label}
                        parentMenuOpen={parentMenuOpen}
                        disabled={item.disabled}
                        direction={direction}
                        divider
                    >
                        {renderMenuItems(item.children, parentMenuOpen)}
                    </NestedMenuItem>
                ) : (
                    <NestedMenuItemMobile
                        key={`${index}-${item.label}`}
                        label={item.label}
                        parentMenuOpen={parentMenuOpen}
                        disabled={item.disabled}
                        divider
                    >
                        {renderMenuItems(item.children, parentMenuOpen)}
                    </NestedMenuItemMobile>
                )
            ) : (
                <MenuItem
                    key={`${index}-${item.label}`}
                    onClick={() => handleItemClick(item)}
                    disabled={item.disabled}
                    divider
                >
                    {item.ItemIcon ? (
                        <ListItemIcon>
                            <item.ItemIcon fontSize='large' sx={{ color: 'black' }} />
                        </ListItemIcon>
                    ) : null}
                    <ListItemText disableTypography>{item.label}</ListItemText>
                </MenuItem>
            )
        );
    };

    return (
        <div>
            <Tooltip title={tooltipTitle || ''}>
                <IconButton
                    id={buttonId}
                    onClick={handleIconClick}
                    aria-controls={open ? menuId : undefined}
                    aria-haspopup='true'
                    aria-expanded={open ? 'true' : undefined}
                >
                    <Icon fontSize='large' sx={{ color: 'black' }} />
                </IconButton>
            </Tooltip>
            <Menu
                id={menuId}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorEl={anchorEl}
                MenuListProps={{
                    'aria-labelledby': buttonId,
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                {...menuProps}
            >
                {renderMenuItems(menuItems, Boolean(anchorEl))}
            </Menu>
        </div>
    );
};

export default NestedMenu;
